/**
 * ResourceLibraryCallout — reusable "Critical Issues + Resource Library link" block.
 *
 * Drop on Music Scene, Calendar, Home, City Guides — anywhere the community is gathering.
 * The idea: every place people land to find joy / events should also know how to reach help.
 *
 * Variants:
 *   • full    → big card with copy, icons, and CTA (default — for landing surfaces)
 *   • compact → 1-line inline link (footers of sub-pages)
 *
 * Optional slots:
 *   • `intro` — page-supplied framing INSIDE the same gradient panel, above "We gather to celebrate"
 *   • `outro` — page-supplied framing inside the same panel, between the emergency contacts
 *     and the final "Open the Resource Library" CTA (used on the Home page so the broader
 *     "Resource Library / Knowledge is protection" framing sits next to the CTA button).
 */
import { Link } from "react-router-dom";
import { Shield, ArrowRight, Wine, Eye, ScanLine, AlertCircle } from "lucide-react";

const ResourceLibraryCallout = ({ variant = "full", className = "", intro = null, outro = null }) => {
  if (variant === "compact") {
    return (
      <div className={`flex flex-wrap items-center gap-2 text-xs text-[#5C5C5C] ${className}`}>
        <Shield className="h-3.5 w-3.5 text-[#2E5C55] flex-shrink-0" />
        <span>
          THE WALL is also a safety resource —{" "}
          <Link
            to="/resources"
            className="font-semibold text-[#2E5C55] hover:underline"
            data-testid="resource-library-inline-link"
          >
            visit the Resource Library →
          </Link>
        </span>
      </div>
    );
  }

  return (
    <section
      className={`rounded-2xl overflow-hidden border-2 border-[#2E5C55]/20 bg-gradient-to-br from-[#F4F8F6] to-[#E8F0EC] ${className}`}
      data-testid="resource-library-callout"
    >
      <div className="p-6 sm:p-8">
        {/* Optional intro slot — page-supplied framing inside the same gradient panel */}
        {intro && (
          <div className="mb-6 pb-6 border-b border-[#2E5C55]/15" data-testid="resource-library-intro">
            {intro}
          </div>
        )}

        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 bg-white rounded-2xl p-3 shadow-sm border border-[#2E5C55]/15">
            <Shield className="h-7 w-7 text-[#2E5C55]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#E76F51]">
                Important · Please Read
              </span>
            </div>
            <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl font-semibold text-[#1A1A1A] leading-tight">
              We gather to celebrate.
              <br className="hidden sm:block" />
              <span className="text-[#2E5C55]"> We also gather to look out for one another.</span>
            </h3>
          </div>
        </div>

        <p className="text-sm sm:text-base text-[#1A1A1A]/80 leading-relaxed mb-4 sm:ml-[68px]">
          Drink spiking, knowing the signs of manipulation, image-based abuse, coercive partners, isolation tactics —
          these don't only happen "somewhere else." They happen at the bars, parties, and shows
          we love most. THE WALL is built on the premise that the people who show up to enjoy
          live music also need to know <strong>what to watch for</strong>,
          <strong> where to go</strong> if something happens, and <strong>how to look out
          for the person next to them.</strong>
        </p>

        <div className="grid sm:grid-cols-3 gap-3 mb-5 sm:ml-[68px]">
          {/* Pillar 1 — Drink Spiking */}
          <div className="bg-white/70 rounded-xl p-3 border border-rose-200">
            <div className="flex items-center gap-2 mb-1">
              <Wine className="h-4 w-4 text-rose-600" />
              <span className="text-xs font-semibold text-[#1A1A1A]">Drink Spiking is Surging</span>
            </div>
            <p className="text-[11px] text-[#5C5C5C] leading-snug">
              Conservatively, <strong>1 in 10 women</strong> have had a drink spiked — and the real
              number sits above it, since most victims never find out. GHB clears the body in ~12 hours
              — know the signs, act fast.
            </p>
          </div>

          {/* Pillar 2 — Know the Signs */}
          <div className="bg-white/70 rounded-xl p-3 border border-teal-200">
            <div className="flex items-center gap-2 mb-1">
              <Eye className="h-4 w-4 text-teal-600" />
              <span className="text-xs font-semibold text-[#1A1A1A]">Know the Signs</span>
            </div>
            <p className="text-[11px] text-[#5C5C5C] leading-snug">
              Gaslighting · DARVO · love bombing · isolation — manipulation patterns to spot
              before they escalate. For yourself, and the person next to you.
            </p>
          </div>

          {/* Pillar 3 — Image-Based Abuse */}
          <div className="bg-white/70 rounded-xl p-3 border border-violet-200">
            <div className="flex items-center gap-2 mb-1">
              <ScanLine className="h-4 w-4 text-violet-600" />
              <span className="text-xs font-semibold text-[#1A1A1A]">Image-Based Abuse</span>
            </div>
            <p className="text-[11px] text-[#5C5C5C] leading-snug">
              Non-consensual photos, deepfakes, and "revenge" sharing are crimes in WA.
              Know your rights and the steps to take if you're targeted.
            </p>
          </div>
        </div>

        {/* "If something happens" emergency contacts — verified Pierce County, WA lines.
            Collapsed by default so the 3 pillars above (Drink Spiking / Know the
            Signs / Image-Based Abuse) stay the lead, and the dense list of hotlines
            is one tap away instead of dominating the page. */}
        <details className="sm:ml-[68px] mb-5 bg-white rounded-xl border border-[#2E5C55]/20 overflow-hidden group" data-testid="emergency-contacts-details">
          <summary className="bg-[#2E5C55] text-white px-3 py-2 flex items-center gap-2 cursor-pointer list-none hover:bg-[#234a44] transition-colors" data-testid="emergency-contacts-summary">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <p className="text-xs font-semibold uppercase tracking-wider flex-1 m-0">
              If something happens — Pierce County, WA
            </p>
            <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 transition-transform group-open:rotate-90" />
          </summary>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs text-[#1A1A1A]">
            <div className="flex items-start gap-2">
              <span className="text-[#C0392B] font-bold flex-shrink-0">→</span>
              <div>
                <strong>Life-threatening emergency:</strong>{" "}
                <a href="tel:911" className="text-[#2E5C55] underline font-semibold" data-testid="emergency-911">911</a>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#C0392B] font-bold flex-shrink-0">→</span>
              <div>
                <strong>Suicide &amp; Crisis Lifeline:</strong>{" "}
                <a href="tel:988" className="text-[#2E5C55] underline font-semibold" data-testid="emergency-988">988</a>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#E76F51] font-bold flex-shrink-0">→</span>
              <div>
                <strong>Pierce County Crisis Line (24/7):</strong>{" "}
                <a href="tel:18005767764" className="text-[#2E5C55] underline font-semibold" data-testid="emergency-pierce-crisis">
                  1-800-576-7764
                </a>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#E76F51] font-bold flex-shrink-0">→</span>
              <div>
                <strong>Sexual Assault Center:</strong>{" "}
                <a href="tel:18007567273" className="text-[#2E5C55] underline font-semibold" data-testid="emergency-sa-center">
                  1-800-756-7273
                </a>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#5B8FB9] font-bold flex-shrink-0">→</span>
              <div>
                <strong>Crisis Text Line:</strong> text{" "}
                <span className="text-[#2E5C55] font-semibold">HOME</span> to{" "}
                <a href="sms:741741?body=HOME" className="text-[#2E5C55] underline font-semibold" data-testid="emergency-text-home">
                  741741
                </a>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#5B8FB9] font-bold flex-shrink-0">→</span>
              <div>
                <strong>NAMI Mental-Health Support:</strong> text{" "}
                <span className="text-[#2E5C55] font-semibold">NAMI</span> to{" "}
                <a href="sms:741741?body=NAMI" className="text-[#2E5C55] underline font-semibold" data-testid="emergency-text-nami">
                  741741
                </a>
              </div>
            </div>
          </div>
          <div className="px-3 pb-3 -mt-1">
            <p className="text-[10px] text-[#5C5C5C] italic">
              At a venue? Tell the bartender or staff first — most are trained to step in safely. Then call.
            </p>
          </div>
        </details>

        <div className="sm:ml-[68px]">
          {outro && (
            <div className="mb-5 pb-5 border-t border-[#2E5C55]/15 pt-5" data-testid="resource-library-outro">
              {outro}
            </div>
          )}
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 bg-[#2E5C55] hover:bg-[#234a44] text-white px-5 py-3 rounded-xl text-sm font-semibold transition-colors"
            data-testid="resource-library-cta-button"
          >
            Find more in the Resource Library
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ResourceLibraryCallout;

